(function() {
  'use strict';

  var JST_OFFSET_MS = 9 * 60 * 60 * 1000;
  var DAY_MS = 24 * 60 * 60 * 1000;
  var WEEK_MS = 7 * DAY_MS;
  var MAX_TIMEOUT_MS = 2147483647;
  var SECTION_SELECTOR = '[data-event-section]';
  var BLOCK_SELECTOR = '[data-event-block]';
  var GLOBAL_DATE_FORMATTER = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo', year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false
  });

  function parseClock(value) {
    var match = /^(\d{2}):(\d{2})$/.exec(String(value || '').trim());
    if (!match) return null;
    var hour = Number(match[1]);
    var minute = Number(match[2]);
    if (hour > 23 || minute > 59) return null;
    return { hour: hour, minute: minute };
  }

  function parseJstDateTime(value) {
    var input = String(value || '').trim();
    var match = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?(?:\+09:00)?$/.exec(input);
    if (!match) return null;
    if (input.indexOf('T') !== -1 && !/\+09:00$/.test(input)) return null;

    var year = Number(match[1]);
    var month = Number(match[2]);
    var day = Number(match[3]);
    var hour = Number(match[4]);
    var minute = Number(match[5]);
    var second = Number(match[6] || 0);
    if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59 || second > 59) return null;

    var epoch = Date.UTC(year, month - 1, day, hour - 9, minute, second);
    var check = new Date(epoch + JST_OFFSET_MS);
    if (
      check.getUTCFullYear() !== year ||
      check.getUTCMonth() !== month - 1 ||
      check.getUTCDate() !== day ||
      check.getUTCHours() !== hour ||
      check.getUTCMinutes() !== minute ||
      check.getUTCSeconds() !== second
    ) return null;

    return epoch;
  }

  function parseEpochSeconds(value) {
    var input = String(value).trim();
    if (!/^[+-]?\d+$/.test(input)) return null;
    var seconds = Number(input);
    var milliseconds = seconds * 1000;
    if (!Number.isSafeInteger(seconds) || !Number.isSafeInteger(milliseconds) || !Number.isFinite(new Date(milliseconds).getTime())) return null;
    return milliseconds;
  }

  function specificTime(dataset, epochName, legacyName) {
    return Object.prototype.hasOwnProperty.call(dataset, epochName)
      ? parseEpochSeconds(dataset[epochName])
      : parseJstDateTime(dataset[legacyName]);
  }

  function getJstWeekPosition(nowMs) {
    var shifted = new Date(nowMs + JST_OFFSET_MS);
    return shifted.getUTCDay() * DAY_MS + shifted.getUTCHours() * 3600000 + shifted.getUTCMinutes() * 60000 + shifted.getUTCSeconds() * 1000 + shifted.getUTCMilliseconds();
  }

  function nextWeeklyBoundary(nowMs, boundaryPosition) {
    var delta = boundaryPosition - getJstWeekPosition(nowMs);
    if (delta <= 0) delta += WEEK_MS;
    return nowMs + delta;
  }

  function evaluateWeekly(block, nowMs) {
    var startClock = parseClock(block.dataset.weeklyStartTime);
    var endClock = parseClock(block.dataset.weeklyEndTime);
    var startDay = Number(block.dataset.weeklyStartDay);
    var endDay = Number(block.dataset.weeklyEndDay);
    if (!startClock || !endClock || !Number.isInteger(startDay) || !Number.isInteger(endDay) || startDay < 0 || startDay > 6 || endDay < 0 || endDay > 6) {
      return { active: false, state: 'invalid', nextBoundary: null };
    }

    var startPosition = startDay * DAY_MS + startClock.hour * 3600000 + startClock.minute * 60000;
    var endPosition = endDay * DAY_MS + endClock.hour * 3600000 + endClock.minute * 60000;
    if (startPosition === endPosition) return { active: false, state: 'invalid', nextBoundary: null };
    var currentPosition = getJstWeekPosition(nowMs);
    var active = startPosition < endPosition
      ? currentPosition >= startPosition && currentPosition < endPosition
      : currentPosition >= startPosition || currentPosition < endPosition;
    return {
      active: active,
      state: active ? 'active' : 'inactive',
      start: active ? null : nextWeeklyBoundary(nowMs, startPosition),
      end: active ? nextWeeklyBoundary(nowMs, endPosition) : null,
      nextBoundary: Math.min(nextWeeklyBoundary(nowMs, startPosition), nextWeeklyBoundary(nowMs, endPosition))
    };
  }

  function evaluateSpecific(block, nowMs) {
    var start = specificTime(block.dataset, 'specificStartEpoch', 'specificStart');
    var end = specificTime(block.dataset, 'specificEndEpoch', 'specificEnd');
    if (start === null || end === null || end <= start) return { active: false, state: 'invalid', nextBoundary: null, start: start, end: end };
    var explicitStart = String(block.dataset.announcementStart || '').trim();
    var explicitEnd = String(block.dataset.announcementEnd || '').trim();
    var epochStartPresent = Object.prototype.hasOwnProperty.call(block.dataset, 'announcementStartEpoch');
    var epochEndPresent = Object.prototype.hasOwnProperty.call(block.dataset, 'announcementEndEpoch');
    var bounded = Boolean(explicitStart || explicitEnd || epochStartPresent || epochEndPresent);
    var announcementStart = epochStartPresent ? parseEpochSeconds(block.dataset.announcementStartEpoch) : explicitStart ? parseJstDateTime(explicitStart) : start;
    var announcementEnd = epochEndPresent ? parseEpochSeconds(block.dataset.announcementEndEpoch) : explicitEnd ? parseJstDateTime(explicitEnd) : end;
    if (announcementStart === null || announcementEnd === null || announcementStart > start || announcementEnd < end) {
      return { active: false, state: 'invalid', nextBoundary: null, start: start, end: end, bounded: bounded };
    }
    var state = nowMs < start ? 'before' : nowMs < end ? 'active' : 'ended';
    var boundaries = [start, end];
    if (bounded) boundaries.push(announcementStart, announcementEnd + 1);
    var nextBoundary = boundaries.filter(function(value) { return value > nowMs; }).sort(function(a, b) { return a - b; })[0] || null;
    return { active: state === 'active', state: state, nextBoundary: nextBoundary, start: start, end: end, bounded: bounded, announcementStart: announcementStart, announcementEnd: announcementEnd };
  }

  function evaluateBlock(block, nowMs) {
    if (block.dataset.eventEnabled !== 'true') return { active: false, state: 'disabled', nextBoundary: null };
    if (block.dataset.scheduleMode === 'always') return { active: true, state: 'active', nextBoundary: null };
    if (block.dataset.scheduleMode === 'weekly') return evaluateWeekly(block, nowMs);
    if (block.dataset.scheduleMode === 'specific') return evaluateSpecific(block, nowMs);
    return { active: false, state: 'invalid', nextBoundary: null };
  }

  function removeBottomNotice(section) {
    (section.__eventNoticeBottomEntries || []).forEach(function(entry) { entry.node.remove(); });
    section.__eventNoticeBottomEntries = [];
  }

  function shouldShowBottomNotice(result, forceEditorPreview) {
    return Boolean(result && (result.active || forceEditorPreview));
  }

  function createBottomNotice(section, block, forceEditorPreview) {
    var notice = document.createElement('aside');
    notice.className = 'event-notice__bottom weekend-event-notice__bottom';
    notice.dataset.eventBottomOwner = section.dataset.sectionId;
    notice.dataset.weekendEventBottomOwner = section.dataset.sectionId;
    if (forceEditorPreview) notice.dataset.eventEditorPreview = 'true';
    var styles = window.getComputedStyle(section);
    ['--event-background', '--event-text', '--event-accent', '--event-button-background', '--event-button-text', '--event-radius', '--event-border-width', '--event-border'].forEach(function(name) {
      notice.style.setProperty(name, styles.getPropertyValue(name));
    });
    var title = document.createElement('p');
    title.className = 'event-notice__bottom-title weekend-event-notice__bottom-title';
    title.textContent = block.dataset.bottomTitle || 'イベント開催中';
    notice.appendChild(title);
    if (block.dataset.bottomText) {
      var text = document.createElement('p');
      text.className = 'event-notice__bottom-text weekend-event-notice__bottom-text';
      text.textContent = block.dataset.bottomText;
      notice.appendChild(text);
    }
    return notice;
  }

  function renderBottomNotices(section, visible, preview) {
    var previous = section.__eventNoticeBottomEntries || [];
    var target = null;
    if (section.dataset.showBottomNotice === 'true') {
      try { target = document.querySelector(section.dataset.bottomTarget || '[data-weekend-event-bottom-target]'); }
      catch (error) { target = null; }
    }
    var desired = target ? visible.filter(function(item) { return shouldShowBottomNotice(item.result, preview && item.preview); }) : [];
    previous.forEach(function(entry) {
      if (!desired.some(function(item) { return item.block === entry.block; })) entry.node.remove();
    });
    var next = [];
    var sameOrder = previous.length === desired.length && previous.every(function(entry, index) { return entry.block === desired[index].block && entry.node.parentNode === target; });
    desired.forEach(function(item) {
      var existing = previous.find(function(entry) { return entry.block === item.block; });
      var previewOnly = preview && item.preview && !item.result.active;
      var node = existing ? existing.node : createBottomNotice(section, item.block, previewOnly);
      if (previewOnly) node.dataset.eventEditorPreview = 'true';
      else delete node.dataset.eventEditorPreview;
      if (!sameOrder) target.appendChild(node);
      next.push({ block: item.block, node: node });
    });
    section.__eventNoticeBottomEntries = next;
  }

  function updateCountdown(block, result, nowMs) {
    var countdown = block.querySelector('[data-event-countdown]');
    if (!countdown) return false;
    var label = countdown.querySelector('[data-event-countdown-label]');
    var values = countdown.querySelectorAll('[data-event-countdown-value]');
    var enabled = block.dataset.countdownEnabled === 'true';
    var target = null;

    if (result.state === 'before' || result.state === 'inactive') {
      target = result.start;
      label.textContent = block.dataset.labelBefore || 'イベント開始まで';
    } else if (result.state === 'active') {
      target = result.end;
      label.textContent = block.dataset.labelActive || 'イベント終了まで';
    } else if (result.state === 'ended') {
      label.textContent = block.dataset.labelEnded || 'このイベントは終了しました';
    }

    if (!Number.isFinite(target)) target = null;
    countdown.hidden = !enabled || result.state === 'invalid' || result.state === 'disabled' || (target === null && result.state !== 'ended');
    countdown.classList.toggle('event-notice__countdown--ended', result.state === 'ended');
    values.forEach(function(value) { value.parentElement.hidden = target === null; });
    if (!enabled || target === null || !Number.isFinite(target)) return false;

    var remaining = Math.max(0, target - nowMs);
    var totalSeconds = Math.floor(remaining / 1000);
    var parts = [Math.floor(totalSeconds / 86400), Math.floor(totalSeconds % 86400 / 3600), Math.floor(totalSeconds % 3600 / 60), totalSeconds % 60];
    values.forEach(function(value, index) { value.textContent = String(parts[index]).padStart(2, '0'); });
    return remaining > 0;
  }

  function updateGlobalPresentation(block, result) {
    if (block.dataset.globalEvent !== 'true') return;
    function setHidden(node, hidden) {
      if (node && node.hidden !== hidden) node.hidden = hidden;
    }
    var state = block.hidden || ['before', 'active'].indexOf(result.state) === -1 ? null : result.state;
    var before = state === 'before';
    var active = state === 'active';
    var status = block.querySelector('[data-global-event-status]');
    var label = block.querySelector('[data-global-event-status-label]');
    var date = block.querySelector('[data-global-event-date]');
    var beforeHint = block.querySelector('[data-global-event-before-hint]');
    var countdown = block.querySelector('[data-event-countdown]');
    var promotions = block.querySelectorAll('[data-global-event-promotion]');
    var boundary = before ? result.start : active ? result.end : null;

    setHidden(status, !state);
    setHidden(beforeHint, !before);
    promotions.forEach(function(promotion) { setHidden(promotion, !before && !active); });
    if (!state) setHidden(countdown, true);

    if (label && state) {
      var statusLabel = (before ? block.dataset.globalStatusBefore : block.dataset.globalStatusActive) || '';
      if (label.textContent !== statusLabel) label.textContent = statusLabel;
    }
    if (date) {
      if (!Number.isFinite(boundary)) {
        setHidden(date, true);
      } else {
        var prefix = before ? block.dataset.globalDateBefore : block.dataset.globalDateActive;
        var formatted = GLOBAL_DATE_FORMATTER.format(new Date(boundary));
        var dateText = (prefix ? prefix + ' ' : '') + formatted + ' JST';
        var iso = new Date(boundary).toISOString();
        if (date.textContent !== dateText) date.textContent = dateText;
        if (date.getAttribute('datetime') !== iso) date.setAttribute('datetime', iso);
        setHidden(date, false);
      }
    }
  }

  function setEditorBadge(block, visible) {
    var badge = block.querySelector('[data-event-editor-badge]');
    if (badge) badge.hidden = !visible;
  }

  function isEligible(block, result, sectionEnabled, nowMs) {
    if (!sectionEnabled || result.state === 'disabled' || result.state === 'invalid') return false;
    if (result.active) return true;
    if (result.bounded) {
      if (result.state === 'before') return result.announcementStart <= nowMs;
      return block.dataset.displayMode === 'lifecycle' && nowMs <= result.announcementEnd;
    }
    return block.dataset.displayMode === 'lifecycle';
  }

  function scheduleNextEvaluation(section, boundary, nowMs, tickCountdown) {
    if (section.__eventNoticeTimer) window.clearTimeout(section.__eventNoticeTimer);
    section.__eventNoticeTimer = null;
    var next = boundary;
    if (tickCountdown) {
      var nextSecond = nowMs + (1000 - nowMs % 1000);
      if (next === null || nextSecond < next) next = nextSecond;
    }
    if (next === null) return;
    var delay = Math.max(1, Math.min(next - nowMs, MAX_TIMEOUT_MS));
    section.__eventNoticeTimer = window.setTimeout(function() { evaluateSection(section); }, delay);
  }

  function evaluateSection(section) {
    var blocks = Array.prototype.slice.call(section.querySelectorAll(BLOCK_SELECTOR));
    var nowMs = Date.now();
    var enabled = section.dataset.sectionEnabled === 'true';
    var designMode = Boolean(window.Shopify && window.Shopify.designMode);
    var allowEditorPreview = designMode && section.dataset.editorPreview === 'true';
    var multi = section.dataset.eventMulti === 'true';
    var visible = [];
    var nextBoundary = null;

    blocks.forEach(function(block) {
      var result = evaluateBlock(block, nowMs);
      block.__eventNoticeResult = result;
      var eligible = isEligible(block, result, enabled, nowMs);
      if (eligible) visible.push({ block: block, result: result, preview: false });
      if (enabled && result.nextBoundary !== null && (nextBoundary === null || result.nextBoundary < nextBoundary)) nextBoundary = result.nextBoundary;
    });

    var selected = section.__eventNoticeSelectedBlock;
    var selectedPreview = designMode && selected && section.contains(selected);
    if (!multi && visible.length > 1) {
      var preferred = visible.find(function(item) { return item.result.active; }) || visible[0];
      visible = [preferred];
    }
    if (selectedPreview && !visible.some(function(item) { return item.block === selected; })) {
      visible.push({ block: selected, result: selected.__eventNoticeResult, preview: true });
    } else if (allowEditorPreview && !visible.length) {
      var fallback = blocks.find(function(block) { return block.dataset.eventEnabled === 'true'; }) || blocks[0];
      if (fallback) visible.push({ block: fallback, result: fallback.__eventNoticeResult, preview: true });
    }
    if (multi && allowEditorPreview) blocks.forEach(function(block) {
      if (block.dataset.eventEnabled === 'true' && block.__eventNoticeResult.state === 'invalid' && !visible.some(function(item) { return item.block === block; })) {
        visible.push({ block: block, result: block.__eventNoticeResult, preview: true });
      }
    });
    if (allowEditorPreview) visible.forEach(function(item) {
      if (!item.result.active && !item.result.bounded && item.block.dataset.displayMode === 'lifecycle') item.preview = true;
    });
    if (!multi && selectedPreview) visible = [{ block: selected, result: selected.__eventNoticeResult, preview: true }];
    if (selectedPreview) visible.forEach(function(item) { if (item.block === selected) item.preview = true; });
    visible = visible.filter(function(item) {
      if (item.block.dataset.globalEvent !== 'true') return true;
      return (item.result.state === 'before' || item.result.state === 'active') && isEligible(item.block, item.result, enabled, nowMs);
    });
    visible.sort(function(a, b) { return blocks.indexOf(a.block) - blocks.indexOf(b.block); });
    var tick = false;
    blocks.forEach(function(block) {
      var item = visible.find(function(candidate) { return candidate.block === block; });
      block.hidden = !item;
      if (block.dataset.globalEvent === 'true') block.dataset.eventState = block.__eventNoticeResult.state;
      var badge = Boolean(item && item.preview && designMode);
      setEditorBadge(block, badge);
      var warning = block.querySelector('[data-event-editor-warning]');
      if (warning) warning.hidden = !(badge && item.result.state === 'invalid');
      if (item) {
        block.dataset.eventState = item.result.state;
        if (updateCountdown(block, item.result, nowMs)) tick = true;
      } else {
        var countdown = block.querySelector('[data-event-countdown]');
        if (countdown) countdown.hidden = true;
      }
      updateGlobalPresentation(block, block.__eventNoticeResult);
    });
    section.hidden = visible.length === 0;
    renderBottomNotices(section, visible, allowEditorPreview);
    scheduleNextEvaluation(section, nextBoundary, nowMs, tick);
  }

  function initialize(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var sections = [];
    if (scope.matches && scope.matches(SECTION_SELECTOR)) sections.push(scope);
    sections = sections.concat(Array.prototype.slice.call(scope.querySelectorAll(SECTION_SELECTOR)));
    sections.forEach(evaluateSection);
  }

  function destroySection(section) {
    if (section.__eventNoticeTimer) window.clearTimeout(section.__eventNoticeTimer);
    section.__eventNoticeTimer = null;
    section.__eventNoticeSelectedBlock = null;
    removeBottomNotice(section);
  }

  function destroy(root) {
    if (!root) return;
    var sections = [];
    if (root.matches && root.matches(SECTION_SELECTOR)) sections.push(root);
    if (root.querySelectorAll) sections = sections.concat(Array.prototype.slice.call(root.querySelectorAll(SECTION_SELECTOR)));
    sections.forEach(destroySection);
  }

  function handleBlockSelect(event) {
    var block = event.target.closest ? event.target.closest(BLOCK_SELECTOR) : null;
    if (!block) return;
    var section = block.closest(SECTION_SELECTOR);
    if (!section) return;
    section.__eventNoticeSelectedBlock = block;
    evaluateSection(section);
  }

  function handleBlockDeselect(event) {
    var block = event.target.closest ? event.target.closest(BLOCK_SELECTOR) : null;
    if (!block) return;
    var section = block.closest(SECTION_SELECTOR);
    if (!section) return;
    section.__eventNoticeSelectedBlock = null;
    evaluateSection(section);
  }

  if (typeof document !== 'undefined' && typeof window !== 'undefined') {
    var controller = window.ChooMiaEventNoticeController;
    if (controller && typeof controller.initialize === 'function') {
      controller.initialize(document);
    } else {
      controller = {
        destroy: destroy,
        initialize: initialize
      };
      window.ChooMiaEventNoticeController = controller;
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function() { controller.initialize(document); });
      else controller.initialize(document);
      document.addEventListener('shopify:section:load', function(event) { controller.initialize(event.target); });
      document.addEventListener('shopify:section:unload', function(event) { controller.destroy(event.target); });
      document.addEventListener('shopify:block:select', handleBlockSelect);
      document.addEventListener('shopify:block:deselect', handleBlockDeselect);
      document.addEventListener('visibilitychange', function() { if (!document.hidden) controller.initialize(document); });
      window.addEventListener('pageshow', function() { controller.initialize(document); });
    }
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      evaluateBlock: evaluateBlock,
      evaluateSpecific: evaluateSpecific,
      evaluateWeekly: evaluateWeekly,
      isEligible: isEligible,
      parseClock: parseClock,
      parseEpochSeconds: parseEpochSeconds,
      parseJstDateTime: parseJstDateTime,
      shouldShowBottomNotice: shouldShowBottomNotice,
      updateCountdown: updateCountdown
    };
  }
})();
